import React, { useCallback, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { alpha } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Link from "@material-ui/core/Link";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import makeStyles from "@material-ui/styles/makeStyles";
import Typography from "@material-ui/core/Typography";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CheckBox from "@material-ui/core/Checkbox";
import InputAdornment from "@material-ui/core/InputAdornment";
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import MailOutlineIcon from '@material-ui/icons/MailOutline';

import IntlMessages from "src/@jumbo/utils/IntlMessages";
import { sendVerificationOTP } from "src/api/auth";
import Alert from "src/helpers/alert";
import { ROLES } from "src/helpers/constants";
import AuthWrapper from "./AuthWrapper";

const useStyles = makeStyles((theme) => ({
	authThumb: {
		backgroundColor: alpha(theme.palette.primary.main, 0.12),
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: 20,
		[theme.breakpoints.up("md")]: {
			width: "50%",
			order: 2,
		},
	},
	titleRoot: {
		marginBottom: 10,
	},
	textFieldRoot: {
	},
	submitProgress: {
		position: "absolute",
		top: 5,
		left: 46,
	},
	textCapital: {
		textTransform: "capitalize",
	},
	textAcc: {
		textAlign: "center",
		"& a": {
			marginLeft: 4,
		},
	},
	info: {
		textAlign: "center",
		marginBottom: 12,
	},
}));

const SignUp = ({ variant = "default", wrapperVariant = "default" }) => {
	const { tenant } = useSelector(({ auth }) => auth);
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	const [isSubmitting, setSubmitting] = useState(false);
	const classes = useStyles({ variant });
	const [agreeTerms, setAgreeTerms] = useState(false);
	const { role, token } = location.state || {};

	const onSubmit = useCallback(
		async (e) => {
			e.preventDefault();
			
			if (!firstName || !lastName || !email) {
				Alert.error("Please fill in all required fields");
				return;
			}

			if (!agreeTerms) {
				Alert.error("Please agree to the terms and conditions");
				return;
			}

			setSubmitting(true);
			try {
				await sendVerificationOTP({
					email,
					tenantId: tenant.id
				});
				
				// Navigate to verify email page with registration data
				navigate("/verify-email", { 
					state: { 
						firstName,
						lastName,
						email,
						role: role || ROLES.FOUNDER,
						token
					} 
				});
			} catch (error) {
				console.error("Error sending verification OTP:", error);
				Alert.error(error.message || "Failed to send verification code");
			} finally {
				setSubmitting(false);
			}
		},
		[firstName, lastName, email, role, agreeTerms, tenant.id, navigate, token]
	);

	const isContinueDisabled = !firstName || !lastName || !email || !agreeTerms || isSubmitting;
	
	return (
		<AuthWrapper variant={wrapperVariant} screen="signup">
			<Box display="flex" flexDirection="column" alignItems="left" mt={4} style={{ paddingTop: 20, paddingBottom: 20 }}>
				<Typography component="h1" variant="h4" fontWeight="fontWeightBold" align="left" style={{ color: '#446475' }} className={classes.titleRoot}>
					Register as a {role || 'Founder'}
				</Typography>
				<Typography align="left" fontWeight="fontWeightBold" style={{ marginBottom: 16, color: '#446475' }}>
					Already have an account?{' '}
					<Link component={NavLink} to="/login" underline="always">
						Log in
					</Link>
				</Typography>
				<form onSubmit={onSubmit} style={{ width: '100%', maxWidth: 550 }}>
					<Box>
						<Typography variant="body2" style={{ fontWeight: 500, marginBottom: 1 }}>
							First Name <span style={{ color: 'red' }}>*</span>
						</Typography>
						<TextField
							placeholder="Input First Name"
							fullWidth
							required
							autoFocus
							onChange={(event) => setFirstName(event.target.value)}
							value={firstName}
							margin="dense"
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<PersonOutlineIcon color="action" />
									</InputAdornment>
								),
								autoComplete: "given-name",
							}}
							variant="outlined"
							className={classes.textFieldRoot}
						/>
					</Box>
					<Box>
						<Typography variant="body2" style={{ fontWeight: 500, marginBottom: 1 }}>
							Last Name <span style={{ color: 'red' }}>*</span>
						</Typography>
						<TextField
							placeholder="Input Last Name"
							fullWidth
							required
							onChange={(event) => setLastName(event.target.value)}
							value={lastName}
							margin="dense"
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<PersonOutlineIcon color="action" />
									</InputAdornment>
								),
								autoComplete: "family-name",
							}}
							variant="outlined"
							className={classes.textFieldRoot}
						/>
					</Box>
					<Box>
						<Typography variant="body2" style={{ fontWeight: 500, marginBottom: 1 }}>
							Email <span style={{ color: 'red' }}>*</span>
						</Typography>
						<TextField
							placeholder="Input email"
							fullWidth
							required
							onChange={(event) => setEmail(event.target.value?.toLowerCase())}
							value={email}
							margin="dense"
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<MailOutlineIcon color="action" />
									</InputAdornment>
								),
								autoComplete: "email",
							}}
							variant="outlined"
							className={classes.textFieldRoot}
						/>
					</Box>
					<Box display="flex" alignItems="center" mb={2}>
						<CheckBox
							checked={agreeTerms}
							onChange={(e) => setAgreeTerms(e.target.checked)}
							color="primary"
						/>
						<Typography variant="body2" style={{ marginLeft: 8 }}>
							Information you provide when registering will be shared with the host and can be used and shared by them in accordance with their{' '}
							<a
								target="_blank"
								style={{ textDecoration: "underline" }}
								rel="noreferrer"
								href={tenant.tncLink || "https://investormatch.ai/terms-of-service/"}
							>
								Terms
							</a>{' '}and{' '}
							<a
								target="_blank"
								style={{ textDecoration: "underline" }}
								rel="noreferrer"
								href={tenant.privacyPolicyLink || "https://investormatch.ai/privacy-policy/"}
							>
								Privacy Policy
							</a>.
						</Typography>
					</Box>
					<Button
						type="submit"
						variant="contained"
						color="primary"
						fullWidth
						disabled={isContinueDisabled}
						style={{ height: 48, fontWeight: 600 }}
					>
						Continue
					</Button>
					{isSubmitting && <CircularProgress size={24} variant={"indeterminate"} className={classes.submitProgress} />}
				</form>
			</Box>
		</AuthWrapper>
	);
};

export default SignUp;
